import React from 'react';
import {
  Building2,
  X,
  Edit2,
  MapPin,
  Phone,
  Mail,
  Globe,
  User,
  Users,
  FileText,
  Calendar,
  Hash,
  Briefcase
} from 'lucide-react';
import { getBusinessStatusColor, getBusinessStatusLabel } from '../utils/businessStatus';

const BusinessDetailModal = ({ business, onClose, onEdit }) => {
  if (!business) return null;

  const location = [
    business.address,
    business.city,
    business.state,
    business.country,
    business.postal_code
  ].filter(Boolean).join(', ');

  const owner = business.owner_name || [business.owner_first_name, business.owner_last_name].filter(Boolean).join(' ');
  const employees = Array.isArray(business.employees) ? business.employees : [];

  const statusClass = getBusinessStatusColor(business);

  const fieldRows = [
    { icon: Briefcase, label: 'Industry', value: business.industry },
    { icon: Building2, label: 'Type', value: business.type },
    { icon: User, label: 'Owner', value: owner },
    { icon: Users, label: 'Employees', value: business.employee_count || employees.length || null },
    { icon: Hash, label: 'Registration Number', value: business.registration_number },
    { icon: Calendar, label: 'Registration Date', value: business.registration_date }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-900 rounded-glass-lg shadow-glass-xl border border-white/20 max-w-5xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-start space-x-4 min-w-0">
            <div className="w-16 h-16 rounded-glass bg-gradient-primary flex items-center justify-center flex-shrink-0">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white break-words">{business.name}</h2>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {business.status && (
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${statusClass}`}>
                    {getBusinessStatusLabel(business)}
                  </span>
                )}
                {business.industry && (
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {business.industry}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
            <button
              type="button"
              onClick={() => onEdit(business)}
              className="p-2 rounded-glass text-gray-600 dark:text-gray-300 hover:bg-blue-600 hover:text-white transition-colors"
              title="Edit"
            >
              <Edit2 className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-glass text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-112px)]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Organization Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {fieldRows.filter(row => row.value).map(({ icon: Icon, label, value }) => (
                    <div key={label} className="glass rounded-glass p-4 border border-white/20">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
                        <Icon className="w-4 h-4 mr-2 text-accent-primary" />
                        {label}
                      </div>
                      <div className="font-medium text-gray-900 dark:text-white">{value}</div>
                    </div>
                  ))}
                </div>
              </section>

              {business.notes && (
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Notes</h3>
                  <div className="glass rounded-glass p-4 border border-white/20">
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{business.notes}</p>
                  </div>
                </section>
              )}

              {employees.length > 0 && (
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Employees</h3>
                  <div className="space-y-2">
                    {employees.map((employee, index) => (
                      <div key={`${employee.id || employee.name || index}`} className="glass rounded-glass p-4 border border-white/20 flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {[employee.first_name, employee.last_name].filter(Boolean).join(' ') || employee.name || 'Unknown'}
                          </div>
                          {(employee.role || employee.department) && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {[employee.role, employee.department].filter(Boolean).join(', ')}
                            </div>
                          )}
                        </div>
                        <Users className="w-4 h-4 text-gray-400" />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <aside className="space-y-6">
              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Contacts</h3>
                <div className="space-y-3">
                  {business.phone && (
                    <a href={`tel:${business.phone}`} className="flex items-center p-3 glass rounded-glass border border-white/20 hover:border-accent-primary transition-colors">
                      <Phone className="w-4 h-4 mr-3 text-accent-primary" />
                      <span className="text-gray-800 dark:text-gray-200 break-all">{business.phone}</span>
                    </a>
                  )}
                  {business.email && (
                    <a href={`mailto:${business.email}`} className="flex items-center p-3 glass rounded-glass border border-white/20 hover:border-accent-primary transition-colors">
                      <Mail className="w-4 h-4 mr-3 text-accent-primary" />
                      <span className="text-gray-800 dark:text-gray-200 break-all">{business.email}</span>
                    </a>
                  )}
                  {business.website && (
                    <a href={business.website} target="_blank" rel="noopener noreferrer" className="flex items-center p-3 glass rounded-glass border border-white/20 hover:border-accent-primary transition-colors">
                      <Globe className="w-4 h-4 mr-3 text-accent-primary" />
                      <span className="text-gray-800 dark:text-gray-200 break-all">{business.website}</span>
                    </a>
                  )}
                  {!business.phone && !business.email && !business.website && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 p-3 glass rounded-glass">No contact information</div>
                  )}
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Location</h3>
                <div className="glass rounded-glass p-4 border border-white/20">
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 mr-3 text-accent-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-gray-800 dark:text-gray-200">
                        {location || 'No location specified'}
                      </div>
                      {(business.latitude || business.longitude) && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                          {[business.latitude, business.longitude].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {(business.created_at || business.updated_at) && (
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Record</h3>
                  <div className="space-y-2 text-sm">
                    {business.created_at && (
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <FileText className="w-4 h-4 mr-2" />
                        Created: {new Date(business.created_at).toLocaleString()}
                      </div>
                    )}
                    {business.updated_at && (
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <FileText className="w-4 h-4 mr-2" />
                        Updated: {new Date(business.updated_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                </section>
              )}
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetailModal;
